import { useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { INVESTMENTS } from '../../data/investments';
import { estimateContractDailyToll } from '../../utils/contractToll';

/**
 * Shared computation hook for the Finance page. Returns every derived
 * monetary aggregate the tabs need (per-poste monthly charges, today /
 * cumul / projection breakdown, chart data, monthly history).
 *
 * Extracted from Finance.jsx on 28/05/2026 so the page-level orchestrator
 * doesn't have to compute everything just to forward it as props. Each
 * subscriber (OverviewTab / PnlTab / CreditsTab / HistoryTab) calls this
 * hook directly and uses only the slice it cares about.
 */
export default function useFinanceData() {
  const {
    treasury, monthlyRevenues, credits, activeInvestments,
    vehicles, drivers, treasuryHistory, day, month, year, eventLog,
    getSupportMonthlyCost, yearlyReports, activeContracts,
  } = useGameStore();

  // Revenue breakdown
  const monthlyRH = drivers.reduce((sum, d) => sum + (d.employerCost || 0), 0);
  const monthlyCredit = credits.reduce((sum, c) => sum + (c.monthlyPayment || 0), 0);
  const monthlyInterest = credits.reduce(
    (sum, c) => sum + ((c.remainingPrincipal || 0) * (c.annualRate || 0) / 12),
    0
  );
  const monthlyMaintenance = vehicles.reduce((sum, v) => sum + v.maintenancePerMonth, 0);
  const monthlyInsurance = vehicles.reduce((sum, v) => {
    const tier = v.insuranceCoverage || 'standard';
    const mult = tier === 'basic' ? 0.70 : tier === 'premium' ? 1.35 : 1;
    return sum + (v.insurancePerMonth || 0) * mult;
  }, 0);
  const monthlyFixed = 250 + 75 + 25;
  const monthlyFuel = vehicles.reduce((sum, v) => {
    const factor = (v.status === 'active' || v.assignedContractId) ? 1 : 0.05;
    return sum + v.dailyFuel * 22 * factor;
  }, 0);
  const monthlyInvCharges = Object.keys(activeInvestments).reduce((sum, invId) => {
    const inv = INVESTMENTS.find(i => i.id === invId);
    return sum + (inv?.chargeMonthly || 0);
  }, 0);
  const monthlyLeasing = vehicles.reduce((sum, v) => sum + (v.isLeased ? (v.leaseMonthlyPayment || 0) : 0), 0);
  const monthlySupport = (typeof getSupportMonthlyCost === 'function') ? getSupportMonthlyCost() : 0;

  // 12/02/2026 — Retour user prod : les péages doivent apparaître dans la
  // vue Finances. On somme les péages quotidiens estimés des contrats
  // actifs et on projette sur 22 jours ouvrés pour rester cohérent avec
  // les autres lignes du breakdown.
  const dailyToll = (activeContracts || []).reduce((sum, c) => {
    const v = (vehicles || []).find(vv => vv.id === c.vehicleId);
    return sum + estimateContractDailyToll(c, v);
  }, 0);
  const monthlyToll = Math.round(dailyToll * 22);

  // Exceptional charges = sum of negative netCashImpact entries from events
  // resolved this month (current month only).
  const monthEventLog = (eventLog || []).filter(e =>
    e.resolvedAt && e.resolvedAt.month === month && e.resolvedAt.year === year
  );
  const monthlyExceptional = monthEventLog.reduce((sum, e) => sum + Math.max(0, (e.eventCost || 0) - (e.eventBonus || 0)), 0);
  const monthlyExceptionalGains = monthEventLog.reduce((sum, e) => sum + Math.max(0, (e.eventBonus || 0) - (e.eventCost || 0)), 0);

  const totalMonthlyCharges =
    monthlyRH + monthlyCredit + monthlyMaintenance + monthlyInsurance +
    monthlyFixed + monthlyInvCharges + monthlyFuel + monthlyLeasing +
    monthlySupport + monthlyExceptional + monthlyToll;
  const netResult = monthlyRevenues - totalMonthlyCharges;

  // Per-charge breakdown for the 3-col table (today / month-to-date / projected end-of-month).
  // `day` is the current day of the month (1..30). We pro-rata each fixed
  // monthly charge as `value × day / 30`. `today` is the daily smoothed
  // portion = monthly / 22 working days (same divisor used in coreSlice.nextDay).
  const dayOfMonth = day || 1;
  const buildRow = (name, monthlyValue, color) => ({
    name,
    color,
    today: monthlyValue / 22,
    cumul: monthlyValue * (dayOfMonth / 30),
    projection: monthlyValue,
    value: monthlyValue,
  });
  // The total credit row is split into "amortissement" and "intérêts (agios)"
  // for clarity. We keep the visual aggregate row too so the bar chart stays
  // readable.
  const monthlyAmortization = Math.max(0, monthlyCredit - monthlyInterest);

  const chartData = treasuryHistory.slice(-30).map((h, i) => ({
    name: `J${i + 1}`,
    tresorerie: Math.round(h.treasury),
    revenus: h.revenue || 0,
    depenses: h.expenses || 0,
  }));

  // Monthly aggregation for history
  const monthlyHistory = useMemo(() => {
    const months = {};
    treasuryHistory.forEach(h => {
      const key = `M${h.month}`;
      if (!months[key]) months[key] = { name: key, revenus: 0, depenses: 0, count: 0 };
      months[key].revenus += h.revenue || 0;
      months[key].depenses += h.expenses || 0;
      months[key].count++;
    });
    return Object.values(months);
  }, [treasuryHistory]);

  const barData = [
    buildRow('RH chauffeurs', monthlyRH, '#3B82F6'),
    buildRow('Fonctions support', monthlySupport, '#0EA5E9'),
    buildRow('Crédit (amort.)', monthlyAmortization, '#8B5CF6'),
    buildRow('Crédit (agios)', monthlyInterest, '#A78BFA'),
    buildRow('Entretien', monthlyMaintenance, '#F59E0B'),
    buildRow('Assurance', monthlyInsurance, '#10B981'),
    buildRow('Carburant', monthlyFuel, '#EF4444'),
    buildRow('Péages', monthlyToll, '#F97316'),
    buildRow('Fixes', monthlyFixed, '#6B7280'),
    buildRow('Invest.', monthlyInvCharges, '#1F3864'),
    buildRow('Leasing', monthlyLeasing, '#7C3AED'),
    buildRow('Aléas (excep.)', monthlyExceptional, '#DC2626'),
  ].filter(d => d.value > 0);

  const totalDebt = credits.reduce((s, c) => s + c.remainingPrincipal, 0);

  return {
    // raw state
    treasury, credits, day, month, year, yearlyReports,
    monthlyRevenues, monthlyCredit,
    // derived
    totalMonthlyCharges, netResult, dayOfMonth,
    chartData, monthlyHistory, barData,
    monthEventLog, monthlyExceptional, monthlyExceptionalGains,
    totalDebt,
  };
}
